import {
    CreateExtArgs,
    CreateExtensionArgument,
    DalValue,
    DmApis,
    DmStore,
    DocumentDataTypes,
    Extension,
    ExtensionAPI,
    type Store,
    HistoryItem,
    InitializeExtArgs,
    ModelsInitializationType,
    PointerMethods,
    pointerUtils,
    Transaction,
    type ExtensionPublicAPI,
    createFragmentStore,
    FragmentStore
} from '@wix/document-manager-core'
import {
    generateApplicationId,
    getLargestApplicationId,
    isBuilderComponent,
    ReportableError,
    searchRequestId
} from '@wix/document-manager-utils'
import type {
    AppDefinitionId,
    ClientSpecMap,
    ClientSpecMapEntry,
    EditorClientSpecMapEntry,
    MetaSiteClientSpecEntry,
    PageList,
    Pointer,
    WixCodeModel
} from '@wix/document-services-types'
import {guidUtils} from '@wix/santa-core-utils'
import {deepClone} from '@wix/wix-immutable-proxy'
import _ from 'lodash'
import {CSAVE_TAG, type CSaveApi} from './csave/continuousSave'
import type {ModifySiteAppsResponse} from '@wix/ambassador-wix-html-editor-webapp/types'
import type {DocumentServicesModelExtApi} from './documentServicesModel'
import type {PlatformExtensionAPI} from './platform'
import {RESOURCE_TYPES} from '../constants/constants'
import type {SnapshotExtApi} from './snapshots'

const {getGUID} = guidUtils

// should come from platformConstants.APPS.META_SITE.applicationId
const META_SITE_APP_ID = '-666'
const META_SITE_CLIENT_SPEC_MAP_URL = '/_api/msm/v1/meta-site/editor-client-spec-map/'

const {getPointer} = pointerUtils
const pointerType = 'rendererModel'
const clientSpecMapCacheKiller = 'clientSpecMapCacheKiller'
const NO_MATCH: string[] = []
const INDEX_MATCH = [pointerType]

const LOCAL_APP_DEFINITION_IDS = ['dataBinding', 'wix-code']

const preserveLocalApps = (existingCSM: ClientSpecMap, refreshedCSM: ClientSpecMap) => {
    const localApps = _.filter(
        existingCSM,
        app => 'appDefinitionId' in app && _.includes(LOCAL_APP_DEFINITION_IDS, app.appDefinitionId)
    )
    _.forEach(localApps, app => {
        const appDefId = (app as EditorClientSpecMapEntry).appDefinitionId
        const existsInRefreshed = _.some(
            refreshedCSM,
            refreshedApp => 'appDefinitionId' in refreshedApp && refreshedApp.appDefinitionId === appDefId
        )
        if (!existsInRefreshed) {
            const currentLargestId = getLargestApplicationId(refreshedCSM)
            const newApplicationId = generateApplicationId(currentLargestId, guidUtils.random)
            refreshedCSM[String(newApplicationId)] = {
                ...app,
                applicationId: newApplicationId
            } as ClientSpecMapEntry
        }
    })
}

const FIVE_MINUTES = 1000 * 60 * 5

const EVENTS = {
    CLIENT_SPEC_MAP: {
        MIGRATE_APPID_TO_APPDEF: 'MIGRATE_APPID_TO_APPDEF'
    }
}

interface RMApi extends ExtensionAPI {
    siteAPI: {
        getDocumentType(): string
        isTemplate(): boolean
        getInstance(): string
        getRendererModel(): any
        getSiteId(): string
    }
    rendererModel: {
        getInitialPageList(): PageList
        registerToClientSpecMapUpdate(cb: ClientUpdateListener): void
        registerToWixCodeModelUpdate(cb: WixCodeModelUpdateListener): void
        refreshClientSpecMap(notifyRemoteEditors?: boolean): Promise<void>
        updateClientSpecMap(txStore: Store): Promise<any>
        onTransactionApproved(txStore: Store): Promise<void>
        getClientSpecMap(): ClientSpecMap
        getAppComponentDataByWidgetId(appDefinitionId: string, widgetId: string | number): Record<string, any>
        getSiteLanguages(): string[]
        askRemoteEditorsToRefreshClientSpecMap(appDefId?: string): void
        onAppsInstalledRemotely(cb: (appData: any) => void): void
        refreshWixInstance(): Promise<string>
        refreshAppInstance(appId: string): Promise<string>
        isWixInstanceExpired(): boolean
        isAppInstanceExpired(appId: string): boolean
        isAppProvisioned(appDefinitionId: string): boolean
        getTimeUntilWixInstanceExpiration(now: number): number
        getSiteId(): string
        getMetaSiteId(): string
        getUserId(): string
        modifySiteApps(apps: {appsToRevoke: AppDefinitionId[]}, branchId: string): Promise<ModifySiteAppsResponse>
        isAppRevoked(appData: any): boolean
        isBuilderComponentModel(): boolean
        loadPendingBuilderResources(): Promise<void>
        mergeCSMToLastApproved(csm: ClientSpecMap): void
    }
    metaSiteActions: {
        getRevokeActions(appDefIds: AppDefinitionId[]): Record<string, any>
    }
}

type ClientUpdateListener = (clientSpecMap: ClientSpecMap) => void
type WixCodeModelUpdateListener = (wixCodeModel: WixCodeModel) => void

const createMetaSiteClientSpecMapUrl = (msid: string): string => `${META_SITE_CLIENT_SPEC_MAP_URL}${msid}?https=true`

/**
 * @returns {Extension}
 */
const createExtension = ({environmentContext, logger}: CreateExtensionArgument): Extension => {
    const clientUpdateListeners: ClientUpdateListener[] = []
    const wixCodeModelListeners: WixCodeModelUpdateListener[] = []

    const createPointersMethods = ({dal}: DmApis): PointerMethods => {
        const getUrlFormat = () => getPointer('urlFormatModel', pointerType, {innerPath: ['format']})
        const getForbiddenPageUriSEOs = () =>
            getPointer('urlFormatModel', pointerType, {innerPath: ['forbiddenPageUriSEOs']})
        const getClientSpecMapCacheKiller = () => getPointer(clientSpecMapCacheKiller, pointerType)
        const getClientSpecMap = () => getPointer('clientSpecMap', pointerType)
        const getClientSpecMapEntry = (applicationId: string) =>
            getPointer('clientSpecMap', pointerType, {innerPath: [applicationId]})
        const getClientSpecMapEntryByAppDefId = (appDefinitionId: string) => {
            const csm = dal.get(getPointer('clientSpecMap', pointerType))
            const applicationId = _.get(_.find(csm, {appDefinitionId}), ['applicationId'])
            return getClientSpecMapEntry(applicationId)
        }
        const getClientSpecMapEntriesByPredicate = (predicate: (v: any) => any) => {
            const filteredCSM = predicate(dal.get(getPointer('clientSpecMap', pointerType)))
            return _.map(filteredCSM, appData => getClientSpecMapEntry(appData.applicationId))
        }

        const getSiteMetaData = () => getPointer('siteMetaData', pointerType)
        const isBuilderComponentModel = () =>
            getPointer('siteMetaData', pointerType, {innerPath: ['builderComponentModel']})
        const isResponsive = () => getPointer('siteMetaData', pointerType, {innerPath: ['isResponsive']})
        const isAdaptiveMobileOn = () => getPointer('siteMetaData', pointerType, {innerPath: ['adaptiveMobileOn']})
        const getMetaSiteId = () => getPointer('metaSiteId', pointerType)
        const getSiteId = () => getPointer('siteInfo', pointerType, {innerPath: ['siteId']})
        const getLanguageCode = () => getPointer('languageCode', pointerType)
        const getGeo = () => getPointer('geo', pointerType)
        const getUserId = () => getPointer('userId', pointerType)
        const getSiteTitleSEO = () => getPointer('siteInfo', pointerType, {innerPath: ['siteTitleSEO']})
        const getDocumentType = () => getPointer('siteInfo', pointerType, {innerPath: ['documentType']})
        const getApplicationType = () => getPointer('siteInfo', pointerType, {innerPath: ['applicationType']})
        const getPremiumFeatures = () => getPointer('premiumFeatures', pointerType)
        const getPasswordProtectedPages = () =>
            getPointer('passwordProtectedPages', pointerType, {innerPath: ['pages']})
        const getSitePropertiesInfo = () => getPointer('sitePropertiesInfo', pointerType)
        const getCurrency = () => getPointer('sitePropertiesInfo', pointerType, {innerPath: ['currency']})
        const getTimeZone = () => getPointer('sitePropertiesInfo', pointerType, {innerPath: ['timeZone']})
        const getLocale = () => getPointer('sitePropertiesInfo', pointerType, {innerPath: ['locale']})
        const getRegionalLanguage = () => getPointer('sitePropertiesInfo', pointerType, {innerPath: ['language']})
        const getMultilingualInfo = () =>
            getPointer('sitePropertiesInfo', pointerType, {innerPath: ['multilingualInfo']})
        const getLocalesInfo = () => getPointer('localesInfo', pointerType)
        const getSiteDisplayName = () => getPointer('sitePropertiesInfo', pointerType, {innerPath: ['siteDisplayName']})
        const getMediaAuthToken = () => getPointer('mediaAuthToken', pointerType)
        const getWixCodeAppData = () => getPointer('wixCodeModel', pointerType, {innerPath: ['appData']})
        const getRevisionGridAppId = () =>
            getPointer('wixCodeModel', pointerType, {innerPath: ['appData', 'codeAppId']})
        const getWixCodeModel = () => getPointer('wixCodeModel', pointerType)
        const getPageToHashedPassword = () => getPointer('pageToHashedPassword', pointerType, {innerPath: ['pages']})
        const getRoutersPointer = () => getPointer('routers', pointerType)
        const getRoutersConfigMapPointer = () => getPointer('routers', pointerType, {innerPath: ['configMap']})
        const getRouterPointer = (routerId: string) =>
            getPointer('routers', pointerType, {innerPath: ['configMap', routerId]})
        const getPlatformAppsExperiments = () => getPointer('platformAppsExperiments', pointerType)
        const getBlocksExperiments = () => getPointer('blocksExperiments', pointerType)
        const getPageList = () => getPointer('pageList', pointerType)
        return {
            general: {
                getUrlFormat,
                getForbiddenPageUriSEOs,
                getClientSpecMap,
                getSiteMetaData,
                getMetaSiteId,
                isResponsive,
                getPageToHashedPassword,
                getClientSpecMapEntry,
                getClientSpecMapEntryByAppDefId,
                getClientSpecMapEntriesByPredicate,
                getCurrency,
                getRegionalLanguage,
                getTimeZone
            },
            rendererModel: {
                getUrlFormat,
                getForbiddenPageUriSEOs,
                getClientSpecMapCacheKiller,
                getClientSpecMap,
                getClientSpecMapEntry,
                getClientSpecMapEntryByAppDefId,
                getClientSpecMapEntriesByPredicate,
                getSiteMetaData,
                isBuilderComponentModel,
                getPageList,
                getMetaSiteId,
                isResponsive,
                getSiteId,
                getLanguageCode,
                getGeo,
                getUserId,
                getSiteTitleSEO,
                getDocumentType,
                getPremiumFeatures,
                getPasswordProtectedPages,
                getSitePropertiesInfo,
                getCurrency,
                getApplicationType,
                getMultilingualInfo,
                getLocalesInfo,
                getTimeZone,
                getLocale,
                getRegionalLanguage,
                getMediaAuthToken,
                getPageToHashedPassword,
                getSiteDisplayName,
                getPlatformAppsExperiments,
                getBlocksExperiments,
                isAdaptiveMobileOn
            },
            wixCode: {
                getWixCodeModel,
                getRevisionGridAppId,
                getWixCodeAppData
            },
            routers: {
                getRoutersPointer,
                getRoutersConfigMapPointer,
                getRouterPointer
            }
        }
    }

    const idsWithSignature = new Set(['routers', 'siteMetaData', 'wixCodeModel'])
    const getDocumentDataTypes = (): DocumentDataTypes => ({
        [pointerType]: {
            idsWithSignature
        }
    })

    const onAppsInstalledRemotelyCallbacks: ((appData: object) => void)[] = []
    const {fetchFn, serverFacade} = environmentContext
    let remotelyInstalledAppsToNotifySubscribers: ClientSpecMapEntry[] = []
    let pendingAppDefIdsForBuilderResourceLoading: AppDefinitionId[] = []
    const createExtensionAPI = (args: CreateExtArgs): RMApi => {
        const {dal, pointers, coreConfig, extensionAPI, resourceLoader} = args
        const siteExtensionAPI = extensionAPI as DocumentServicesModelExtApi
        let currentClientSpecMapCacheKiller = ''

        const generateClientSpecMapCacheKiller = (appDefId?: string) => {
            currentClientSpecMapCacheKiller = appDefId ? `${getGUID()}$$${appDefId}` : getGUID()
            return currentClientSpecMapCacheKiller
        }
        const getCurrentClientSpecMapCacheKiller = () => currentClientSpecMapCacheKiller
        const setCurrentClientSpecMapCacheKiller = (newVal: string) => {
            currentClientSpecMapCacheKiller = newVal
        }

        const getAppData = (appId: string) => dal.get(pointers.general.getClientSpecMapEntry(appId)) || {}

        const getMetaSiteId = (): string => dal.get(pointers.general.getMetaSiteId())

        const getAppInstanceExpirationTime = (appId: string, now: number): number => {
            const appData = dal.get(pointers.general.getClientSpecMapEntry(appId)) || {}
            const expirationTimestamp = appData.expirationDate
            if (!expirationTimestamp) {
                return now
            }
            const expirationDate = new Date(expirationTimestamp)
            return expirationDate.getTime() - FIVE_MINUTES
        }

        const getTimeUntilAppInstanceExpiration = (appId: string, now: number) =>
            getAppInstanceExpirationTime(appId, now) - now

        const isAppInstanceExpired = (appId: string) => getTimeUntilAppInstanceExpiration(appId, Date.now()) <= 0

        const createFetchError = (response: Response | null, e: Error): ReportableError =>
            new ReportableError({
                message: 'Cannot fetch client spec map',
                errorType: 'CLIENT_SPEC_MAP_FETCH_FAILURE',
                extras: {
                    status: response?.status,
                    requestId: searchRequestId(response),
                    reason: {name: e.name, message: e.message},
                    isWixInstanceExpired: isAppInstanceExpired(META_SITE_APP_ID),
                    newUrl: true
                }
            })

        const fetchCsmResponse = async (): Promise<Response> =>
            await fetchFn(createMetaSiteClientSpecMapUrl(getMetaSiteId()))

        const fetchClientSpecMap: () => Promise<ClientSpecMap> = async () => {
            let response: Response
            const interaction = 'fetchCsmFromMetaSite'
            try {
                coreConfig.logger.interactionStarted(interaction)
                response = await fetchCsmResponse()
                const result = await response!.json()
                coreConfig.logger.interactionEnded(interaction)
                return result
            } catch (e) {
                const err = createFetchError(response! ?? null, e as Error)
                coreConfig.logger.captureError(err)
                throw err
            }
        }

        const mergeCSMToLastApproved = (csm: ClientSpecMap) => {
            const existingCSM = dal.get(pointers.general.getClientSpecMap())
            if (_.isEqual(existingCSM, csm)) {
                return
            }
            const newStore = createFragmentStore()
            newStore.set(pointers.general.getClientSpecMap(), csm)
            if (!dal.tagManager._getAllTags().initialState || _.isEmpty(dal.tagManager._getAllTags().initialState)) {
                dal.mergeToApprovedStore(newStore)
            } else {
                const {snapshots} = extensionAPI as SnapshotExtApi
                const lastCSave = snapshots.getLastSnapshotByTagName(CSAVE_TAG) ?? snapshots.getInitialSnapshot()
                const lastApproved = dal.getLastApprovedSnapshot()
                const snapshot = dal.rebase(newStore, lastApproved.id)
                if (lastCSave === lastApproved) {
                    dal.tagManager.addSnapshot(CSAVE_TAG, snapshot)
                }
                dal.approve(snapshot.id)
            }
            _.forEach(clientUpdateListeners, cb => cb(csm))
        }

        const refreshAppInstance = async (appId: string): Promise<string> => {
            const refreshedClientSpecMap = await fetchClientSpecMap()
            const refreshedEntry = refreshedClientSpecMap[appId]
            if (!refreshedEntry) {
                return getAppData(appId)?.instance
            }

            const entry: MetaSiteClientSpecEntry = _.cloneDeep(getAppData(appId))
            _.assign(entry, _.pick(refreshedClientSpecMap[appId], ['instance', 'expirationDate']))
            if (coreConfig.experimentInstance.isOpen('dm_mergeComponentsToStore')) {
                const existingCSM = dal.get(pointers.general.getClientSpecMap())
                const updatedCSM = {...existingCSM, [appId]: entry}
                mergeCSMToLastApproved(updatedCSM)
            } else {
                dal.set(pointers.general.getClientSpecMapEntry(appId), entry)
            }
            return getAppData(appId)?.instance
        }

        const nonRevoked = (appData: ClientSpecMapEntry) => !_.get(appData, ['permissions', 'revoked'], false)
        const revoked = (appData: ClientSpecMapEntry) => _.get(appData, ['permissions', 'revoked'], false)

        const isAppProvisioned = (appDefinitionId: AppDefinitionId) => {
            const appData = dal.get(pointers.rendererModel.getClientSpecMapEntryByAppDefId(appDefinitionId))
            if (!appData) {
                return false
            }
            return !revoked(appData) && !appData.requiresEditorComponent
        }
        const preserveExistingComponents = (existingCSM: ClientSpecMap) => (cs: Record<string, any>) => {
            const applicationId = _.get(cs, ['applicationId'])
            const existingComponents = _.get(existingCSM, [applicationId, 'components'], [])
            const existingErcComponents = existingComponents.filter(isBuilderComponent)
            if (existingErcComponents.length > 0) {
                const refreshedNonErcComponents = (cs.components ?? []).filter(
                    (component: Record<string, any>) => !isBuilderComponent(component)
                )
                return {
                    ...cs,
                    components: [...existingErcComponents, ...refreshedNonErcComponents]
                }
            }
            return cs
        }
        const getRevokedClientSpecs = (existingCSM: ClientSpecMap, refreshedCSM: ClientSpecMap) =>
            _(refreshedCSM as Record<string, any>)
                .filter(revoked)
                .map(preserveExistingComponents(existingCSM))
                .keyBy('applicationId')
                .value()
        const getNewInstalledClientSpecs = (existingCSM: ClientSpecMap, refreshedCSM: ClientSpecMap) =>
            _(refreshedCSM as Record<string, any>)
                .filter(nonRevoked)
                .filter(cs => _.isNil(existingCSM[_.get(cs, ['applicationId'])]))
                .keyBy('applicationId')
                .value()
        const getReinstalledClientSpecs = (existingCSM: ClientSpecMap, refreshedCSM: ClientSpecMap) =>
            _(refreshedCSM as Record<string, any>)
                .filter(cs => _.get(existingCSM, [_.get(cs, ['applicationId']), 'permissions', 'revoked'], false))
                .filter(nonRevoked)
                .map(preserveExistingComponents(existingCSM))
                .keyBy('applicationId')
                .value()
        const getUpdatedClientSpecs = (existingCSM: ClientSpecMap, refreshedCSM: ClientSpecMap) =>
            _(refreshedCSM as Record<string, any>)
                .filter(nonRevoked)
                .filter(cs => {
                    const applicationId = _.get(cs, ['applicationId'])
                    return (
                        !_.isNil(existingCSM[applicationId]) &&
                        !_.isEqual(
                            _.get(existingCSM, [applicationId, 'version']),
                            _.get(refreshedCSM, [applicationId, 'version'])
                        )
                    )
                })
                .map(preserveExistingComponents(existingCSM))
                .keyBy('applicationId')
                .value()

        // @ts-expect-error
        function getInstance(): string {
            const metaSiteData = getAppData(META_SITE_APP_ID)
            if (metaSiteData.instance) {
                return metaSiteData.instance
            }
        }

        const getDocumentTypeVal = () => dal.get(pointers.rendererModel.getDocumentType())

        const getSiteId = () => dal.get(pointers.rendererModel.getSiteId())
        const getUserId = () => dal.get(pointers.rendererModel.getUserId())

        const askRemoteEditorsToRefreshClientSpecMap = (appDefId?: string) => {
            dal.set(pointers.rendererModel.getClientSpecMapCacheKiller(), {
                cacheKiller: generateClientSpecMapCacheKiller(appDefId)
            })
        }
        const getClientSpecMap = () => dal.get(pointers.rendererModel.getClientSpecMap())

        const getAppComponentDataByWidgetId = (appDefinitionId: string, widgetId: string | number) => {
            const appData = dal.get(pointers.rendererModel.getClientSpecMapEntryByAppDefId(appDefinitionId))
            if (!appData) {
                return
            }

            return appData.components?.find((component: Record<string, any>) => widgetId === component.componentId)
                ?.data
        }

        const getSiteLanguages = () => {
            const mlInfo = dal.get(pointers.rendererModel.getMultilingualInfo())
            if (!mlInfo) {
                return []
            }
            const requiredLanguages = mlInfo.translationLanguages.map((lang: any) => lang.id)
            if (mlInfo.originalLanguage.id) {
                return requiredLanguages.concat(mlInfo.originalLanguage.id)
            }
            return requiredLanguages
        }
        const getRevokeActions = (appDefIds: AppDefinitionId[]) => {
            const csm = getClientSpecMap()
            return appDefIds
                .map(appDefinitionId => {
                    const app = _.find(csm, {appDefinitionId})
                    if (!app?.applicationId) {
                        logger.captureError(
                            new ReportableError({
                                errorType: 'appIdMigrationToAppDefIdError',
                                message: 'error on migration process from appId to appDefId',
                                extras: {source: 'metaSiteActions.getRevokeActions', appDefinitionId}
                            })
                        )
                        return null
                    }
                    return appDefinitionId
                })
                .filter(Boolean)
        }

        const modifySiteApps = async (
            {appsToRevoke}: {appsToRevoke: AppDefinitionId[]},
            branchId: string
        ): Promise<ModifySiteAppsResponse> => {
            const metaSiteActions = {newlyRemovedApps: getRevokeActions(appsToRevoke), newlyAddedApps: []}
            return serverFacade.modifySiteApps(metaSiteActions, siteExtensionAPI.siteAPI.getSiteRevision(), branchId)
        }

        const updateClientSpecMap = async (txStore: DmStore) => {
            const changes = txStore.getValues()
            const clientSpecMapCacheKillerItem = _.find(
                changes,
                e => e?.key?.type === pointerType && e?.key?.id === clientSpecMapCacheKiller
            )
            const received = clientSpecMapCacheKillerItem?.value?.cacheKiller
            const curr = getCurrentClientSpecMapCacheKiller()
            if (clientSpecMapCacheKillerItem && received !== curr) {
                coreConfig.logger.interactionStarted('updateClientSpecMap')
                setCurrentClientSpecMapCacheKiller(received)
                const refreshedCSM = await fetchClientSpecMap()
                const existingCSM: ClientSpecMap = dal.get(pointers.general.getClientSpecMap())
                const updatedClientSpecMap = deepClone(existingCSM)

                const newClientSpecs = getNewInstalledClientSpecs(existingCSM, refreshedCSM)
                const revokedClientSpecs = getRevokedClientSpecs(existingCSM, refreshedCSM)
                const reinstalledClientSpecs = getReinstalledClientSpecs(existingCSM, refreshedCSM)
                const updatedClientSpecs = getUpdatedClientSpecs(existingCSM, refreshedCSM)

                if (dal.get(pointers.rendererModel.isBuilderComponentModel())) {
                    const appsToFetchResourcesFor = Object.values(newClientSpecs)
                        .concat(Object.values(reinstalledClientSpecs), Object.values(updatedClientSpecs))
                        .map(app => app.appDefinitionId)
                        .filter(Boolean) as AppDefinitionId[]

                    if (appsToFetchResourcesFor.length > 0) {
                        pendingAppDefIdsForBuilderResourceLoading.push(...appsToFetchResourcesFor)
                    }
                }

                if (received.includes('$$')) {
                    const parts = received.split('$$')
                    const appDefId = parts.length > 1 ? received.split('$$')[1] : null
                    const appData = _.find(refreshedCSM, {appDefinitionId: appDefId})
                    if (appData) {
                        remotelyInstalledAppsToNotifySubscribers.push(appData as EditorClientSpecMapEntry)
                    }
                } else {
                    remotelyInstalledAppsToNotifySubscribers.push(
                        ..._.uniqBy(_.values(newClientSpecs).concat(_.values(reinstalledClientSpecs)), 'applicationId')
                    )
                }
                _.assign(
                    updatedClientSpecMap,
                    newClientSpecs,
                    revokedClientSpecs,
                    reinstalledClientSpecs,
                    updatedClientSpecs
                )
                coreConfig.logger.interactionEnded('updateClientSpecMap')
                txStore.set(pointers.general.getClientSpecMap(), updatedClientSpecMap)
            }
        }

        const queueVersionedSchemaAppIds = (txStore: DmStore) => {
            const fragmentStore = txStore as unknown as FragmentStore
            if (
                fragmentStore.hasFragmentStore?.(RESOURCE_TYPES.APP_COMPONENT_VERSIONS) &&
                dal.get(pointers.rendererModel.isBuilderComponentModel())
            ) {
                const appVersionsStore = fragmentStore.getFragmentStore(RESOURCE_TYPES.APP_COMPONENT_VERSIONS)!
                const appDefIds = _.uniq(
                    appVersionsStore
                        .keys()
                        .map(ptr => ptr.id)
                        .filter(Boolean)
                ) as AppDefinitionId[]
                if (appDefIds.length > 0) {
                    pendingAppDefIdsForBuilderResourceLoading.push(...appDefIds)
                }
            }
        }

        return {
            siteAPI: {
                getDocumentType: getDocumentTypeVal,
                isTemplate: () => getDocumentTypeVal() === 'Template',
                getInstance,
                getSiteId,
                getRendererModel: () =>
                    _.map(
                        dal.query(pointerType, dal.queryFilterGetters.getRendererModelFilter(pointerType)),
                        (value, id) => ({
                            pointer: getPointer(id, pointerType),
                            value
                        })
                    )
            },
            rendererModel: {
                getInitialPageList: () => dal.get(pointers.rendererModel.getPageList()),
                registerToClientSpecMapUpdate: (cb: ClientUpdateListener) => {
                    if (_.isFunction(cb)) {
                        clientUpdateListeners.push(cb)
                    }
                },
                registerToWixCodeModelUpdate: (cb: WixCodeModelUpdateListener) => {
                    if (_.isFunction(cb)) {
                        wixCodeModelListeners.push(cb)
                    }
                },
                refreshClientSpecMap: async (notifyRemoteEditors = true) => {
                    const existingCSM = dal.get(pointers.general.getClientSpecMap())
                    const refreshedCSM = await fetchClientSpecMap()
                    preserveLocalApps(existingCSM, refreshedCSM)

                    if (
                        !(
                            pointers.rendererModel.isBuilderComponentModel() &&
                            dal.get(pointers.rendererModel.isBuilderComponentModel())
                        )
                    ) {
                        if (coreConfig.experimentInstance.isOpen('dm_mergeComponentsToStore')) {
                            mergeCSMToLastApproved(refreshedCSM)
                        } else {
                            dal.set(pointers.general.getClientSpecMap(), refreshedCSM)
                        }
                        if (notifyRemoteEditors) {
                            askRemoteEditorsToRefreshClientSpecMap()
                        }
                        return
                    }

                    _.forEach(existingCSM, (existingApp, appId) => {
                        const refreshedApp = refreshedCSM[appId]
                        if (refreshedApp) {
                            const existingComponents = (existingApp as EditorClientSpecMapEntry).components ?? []
                            if (existingComponents.length > 0) {
                                refreshedCSM[appId] = {
                                    ...refreshedApp,
                                    components: existingComponents,
                                    dependencies: (existingApp as EditorClientSpecMapEntry).dependencies
                                } as EditorClientSpecMapEntry
                            }
                        }
                    })

                    if (coreConfig.experimentInstance.isOpen('dm_mergeComponentsToStore')) {
                        mergeCSMToLastApproved(refreshedCSM)
                    } else {
                        dal.set(pointers.general.getClientSpecMap(), refreshedCSM)
                    }
                    if (dal.get(pointers.rendererModel.isBuilderComponentModel())) {
                        try {
                            await Promise.all([
                                resourceLoader.loadAll(RESOURCE_TYPES.SCHEMA),
                                resourceLoader.loadAll(RESOURCE_TYPES.COMPONENTS)
                            ])
                        } catch (error) {
                            coreConfig.logger.captureError(
                                new ReportableError({
                                    errorType: 'SCHEMA_OR_COMPONENTS_LOADING_FAILURE',
                                    message: 'Failed to load schemas or components for newly installed remote apps',
                                    extras: {
                                        error: error instanceof Error ? error.message : String(error)
                                    }
                                })
                            )
                        }

                        const {platform} = extensionAPI as PlatformExtensionAPI
                        _.forEach(_.filter(refreshedCSM, 'appDefinitionId'), app => {
                            platform.addBuilderCompAppData((app as EditorClientSpecMapEntry).appDefinitionId)
                        })
                    }
                    if (notifyRemoteEditors) {
                        askRemoteEditorsToRefreshClientSpecMap()
                    }
                },
                getSiteLanguages,
                updateClientSpecMap,
                getClientSpecMap,
                getAppComponentDataByWidgetId,
                refreshWixInstance: async () => refreshAppInstance(META_SITE_APP_ID),
                refreshAppInstance,
                isWixInstanceExpired: () => isAppInstanceExpired(META_SITE_APP_ID),
                isAppInstanceExpired,
                getTimeUntilWixInstanceExpiration: (now: number) =>
                    getTimeUntilAppInstanceExpiration(META_SITE_APP_ID, now),
                askRemoteEditorsToRefreshClientSpecMap,
                onAppsInstalledRemotely: (cb: (appData: any) => void) => {
                    onAppsInstalledRemotelyCallbacks.push(cb)
                },
                getSiteId,
                getMetaSiteId,
                getUserId,
                modifySiteApps,
                isAppRevoked: revoked,
                isAppProvisioned,
                isBuilderComponentModel: () => dal.get(pointers.rendererModel.isBuilderComponentModel()),
                mergeCSMToLastApproved,
                onTransactionApproved: async (txStore: DmStore) => {
                    await updateClientSpecMap(txStore)
                    queueVersionedSchemaAppIds(txStore)
                },
                loadPendingBuilderResources: async () => {
                    if (pendingAppDefIdsForBuilderResourceLoading.length === 0) {
                        return
                    }
                    const appDefIds = _.uniq(pendingAppDefIdsForBuilderResourceLoading)
                    pendingAppDefIdsForBuilderResourceLoading = []

                    const {platform} = extensionAPI as PlatformExtensionAPI
                    try {
                        await platform.loadBuilderResourcesForApps(appDefIds)
                    } catch (error) {
                        coreConfig.logger.captureError(
                            new ReportableError({
                                errorType: 'SCHEMA_LOADING_FAILURE',
                                message: 'Failed to load schemas for newly installed remote apps',
                                extras: {
                                    appDefIds,
                                    error: error instanceof Error ? error.message : String(error)
                                }
                            })
                        )
                    }
                }
            },
            metaSiteActions: {
                getRevokeActions
            }
        }
    }
    const createPublicAPI = ({extensionAPI}: DmApis): ExtensionPublicAPI => {
        const {rendererModel} = extensionAPI as RMApi
        return {
            rendererModel: {
                refreshClientSpecMap: rendererModel.refreshClientSpecMap
            }
        }
    }
    const createFilters = () => ({
        getRendererModelFilter: (namespace: string): string[] => {
            if (namespace === pointerType) {
                return INDEX_MATCH
            }
            return NO_MATCH
        }
    })

    const initialState = {
        [pointerType]: {
            sitePropertiesInfo: {
                multilingualInfo: {
                    originalLanguage: {}
                }
            }
        }
    }

    const createPostTransactionOperations = () => ({
        rendererModel: (documentTransaction: Transaction) => {
            if (!_.isEmpty(remotelyInstalledAppsToNotifySubscribers)) {
                for (const cb of onAppsInstalledRemotelyCallbacks) {
                    cb(remotelyInstalledAppsToNotifySubscribers)
                }

                remotelyInstalledAppsToNotifySubscribers = []
            }
            const rendererModelOperations = documentTransaction.items.filter(e => e?.key?.type === pointerType)
            _.forEach(rendererModelOperations, (item: HistoryItem) => {
                const {
                    value,
                    key: {id: pointerId}
                } = item
                if (pointerId === 'clientSpecMap') {
                    _.forEach(clientUpdateListeners, cb => cb(value))
                }
                if (pointerId === 'wixCodeModel') {
                    _.forEach(wixCodeModelListeners, cb => cb(value))
                }
            })
        }
    })

    const setRendererModelValue = (
        initialStore: DmStore,
        itemPointer: Pointer,
        value: DalValue,
        key: string,
        signatures: Record<string, any>
    ) => {
        const signature = signatures[`rendererModel.${key}`]
        if (idsWithSignature.has(key) && signature) {
            const signedValue = {...value, metaData: _.clone(value.metaData)}
            _.setWith(signedValue, ['metaData', 'sig'], signature, Object)
            initialStore.set(itemPointer, signedValue)
        } else {
            initialStore.set(itemPointer, value)
        }
    }

    const initializeModels = (initialStore: DmStore, initialModels: ModelsInitializationType, {coreConfig}: DmApis) => {
        const {
            rendererModel: initialRendererModelFromServer,
            documentServicesModel: {signatures = {}}
        } = initialModels
        for (const idWithSig of idsWithSignature) {
            if (!initialRendererModelFromServer[idWithSig] && signatures[`rendererModel.${idWithSig}`]) {
                initialRendererModelFromServer[idWithSig] = {}
            }
        }
        _.forEach(initialRendererModelFromServer, (value: DalValue, key) => {
            const itemPointer = getPointer(key, 'rendererModel')
            switch (key) {
                case 'urlFormatModel': {
                    let {forbiddenPageUriSEOs} = value
                    if (_.isArray(forbiddenPageUriSEOs)) {
                        forbiddenPageUriSEOs = _(forbiddenPageUriSEOs)
                            .keyBy()
                            .mapValues(() => true)
                            .value()
                    }

                    const urlFormatModelValue = {...value, forbiddenPageUriSEOs}
                    initialStore.set(itemPointer, urlFormatModelValue)
                    return
                }

                case 'passwordProtectedPages': {
                    const wrappedPasswordProtectedPages = _.isArray(value) ? {pages: value} : value
                    initialStore.set(itemPointer, wrappedPasswordProtectedPages)
                    return
                }

                case 'wixCodeModel': {
                    if (_.isEmpty(value) && coreConfig.experimentInstance.isOpen('dm_wixCodeModelPopulateOnBaseline')) {
                        value = {appData: {}}
                    }
                    setRendererModelValue(initialStore, itemPointer, value, key, signatures)
                    return
                }

                default:
                    setRendererModelValue(initialStore, itemPointer, value, key, signatures)
            }
        })
    }

    const initialize = async ({extensionAPI, eventEmitter, dal, pointers, coreConfig}: InitializeExtArgs) => {
        const {continuousSave} = extensionAPI as CSaveApi
        const {rendererModel} = extensionAPI as RMApi
        continuousSave?.registerToTransactionApproved(rendererModel.onTransactionApproved)
        continuousSave?.registerToTransactionApprovedAfterRebase(rendererModel.loadPendingBuilderResources)

        eventEmitter.on(
            EVENTS.CLIENT_SPEC_MAP.MIGRATE_APPID_TO_APPDEF,
            (data: any, reportErrorOnRemigrationSource?: string) => {
                if (data?.appId && !data?.appDefinitionId) {
                    const specMapEntry = dal.get(pointers.general.getClientSpecMapEntry(data?.appId)) || {}
                    if (specMapEntry?.appDefinitionId) {
                        _.set(data, ['appDefinitionId'], specMapEntry.appDefinitionId)
                    }
                    if (reportErrorOnRemigrationSource) {
                        coreConfig.logger.captureError(
                            new ReportableError({
                                errorType: 'menuUpdateAppDefIdMigration',
                                message: `${reportErrorOnRemigrationSource}: data with appId needed appDefinitionId remigration`,
                                extras: {
                                    appId: data.appId,
                                    migratedAppDefinitionId: data.appDefinitionId
                                }
                            })
                        )
                    }
                }
            }
        )
    }

    return {
        name: 'rendererModel',
        dependencies: new Set(['schemaResourceLoader']),
        createPointersMethods,
        getDocumentDataTypes,
        initialState,
        createExtensionAPI,
        createPublicAPI,
        createFilters,
        createPostTransactionOperations,
        initialize,
        initializeModels,
        EVENTS
    }
}

export {META_SITE_APP_ID, createExtension, pointerType, RMApi, createMetaSiteClientSpecMapUrl, EVENTS}
